import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useMemo } from "react";
import { Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import type { SerializedObject, UnpackResponse } from "../../../api/src/lib";
import type { TasksController } from "../../../api/src/tasks/tasks.controller";
import type { EditableTask, Task } from "../../../api/src/tasks/tasks.service";
import Loading from "../Loading";
import type { CrmUser } from "../../../api/src/users/users.service";
import { CrmUsersRecord, useQueryCrmUsers } from "../firm/lib";
import { useAuthenticatedFetch } from "../lib/api";
import { InlineError } from "../lib/display";
import { getSchemaFieldLabel, nullableNumberSchema } from "../lib/forms";
import FormFieldError from "./FormFieldError";
import SubmitButton from "./SubmitButton";
import { useCreateTaskSubmit } from "./useCreateTaskSubmit";

export type CreateTaskCrm = {
  wealthbox: boolean;
  redtail: boolean;
  slant: boolean;
};

// The create-task form for a record linked to Wealthbox or Redtail, or to one
// of them and Slant at once. A record linked to Slant alone has a different
// field set and its own form, `CreateSlantTask`
// (s-slant-create-task-form-019fd301b735a8cd).
//
// Category lives on Wealthbox and Redtail tasks, so it is always declared here
// and always rendered: every field this schema can reject has a control on
// screen to show the rejection. Stripping it for a record it did not apply to
// left the schema demanding a value nothing supplied, and the form could not be
// submitted or say why (APP-1415).
//
// Only the priority list varies. Slant tasks support an "Urgent" level
// (urgent_priority) on top of the shared Low/Medium/High, so it validates only
// when the record is also Slant-linked; Wealthbox folds it onto High and Redtail
// onto 5.

/**
 * What this form writes. Narrower than `EditableTask` in one place: `assignedTo`
 * is a Wealthbox or Redtail user id, which is numeric, while `EditableTask`
 * spans Slant's opaque string ids too
 * (s-task-assignee-list-source-019fd301825de871). Declaring the narrower type is
 * what lets the assignee stay a coerced number here.
 */
export type EditableWealthboxTask = Omit<EditableTask, "assignedTo"> & {
  assignedTo: number;
};

/**
 * An empty Priority or Due Date control has to submit `null` rather than
 * `undefined`: `EditableTask` picks both fields off `Task`, where each is now
 * declared `| null` rather than optional (APP-1539). `forms.ts`'s shared
 * `optionalStringValueTransform` yields `undefined`, which those fields no
 * longer accept, so the empty case is transformed here instead. Same shape as
 * `nullableStringSchema` in `forms.ts`, including the `.default(null)` that
 * covers a control the advisor never touched — yup skips transforms entirely
 * when the raw value is `undefined`.
 */
function nullableValueTransform<TValue>(value: TValue, originalValue: string) {
  return originalValue === "" ? null : value;
}

export const buildSchema = (
  crm: CreateTaskCrm,
): yup.ObjectSchema<EditableWealthboxTask> =>
  yup.object({
    name: yup.string().required().label("Name"),
    description: yup.string().label("Description"),
    priority: yup
      .string()
      .defined()
      .nullable()
      .default(null)
      .transform(nullableValueTransform)
      .oneOf(
        crm.slant
          ? ["Low", "Medium", "High", "Urgent", null]
          : ["Low", "Medium", "High", null],
      )
      .label("Priority"),
    dueDate: yup
      .date()
      .defined()
      .nullable()
      .default(null)
      .transform(nullableValueTransform)
      .label("Due Date"),
    category: nullableNumberSchema.label("Category"),
    assignedTo: yup.number().required().label("Assignee"),
  });

const CreateTask = ({
  onCreate,
  crm,
  record,
}: {
  onCreate: (data: EditableTask) => Promise<Task | null>;
  crm: CreateTaskCrm;
  record?: CrmUsersRecord;
}) => {
  const schema = useMemo(() => buildSchema(crm), [crm]);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    getValues,
    setValue,
    reset,
  } = useForm<EditableWealthboxTask>({
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const { errorMessage, submit } = useCreateTaskSubmit({ onCreate, reset });

  const {
    isPending: isPendingCategories,
    isError: isErrorCategories,
    data: categoriesData,
  } = useAuthenticatedFetch<
    SerializedObject<UnpackResponse<TasksController["getCategories"]>>
  >("/tasks/categories");

  const categoryOptions = useMemo(
    () => [
      <option key={null} value="">
        Select a category...
      </option>,
      ...(categoriesData?.data ?? [])
        .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
        .map((category) => (
          <option key={category.id} value={category.id}>
            {category.name}
          </option>
        )),
    ],
    [categoriesData?.data],
  );

  const {
    isPending: isPendingAssignees,
    isError: isErrorAssignees,
    isFetched: isFetchedAssignees,
    data: assigneesData,
    // Scoped to the record this form sits on, so the server reads that record's
    // own CRM. A firm with Slant and Wealthbox both connected derives Slant,
    // and its string ids are meaningless to the Wealthbox task this form is
    // about to write — deriving here would leave this form with no usable
    // assignee at all (s-task-assignee-list-source-019fd301825de871, R1).
  } = useQueryCrmUsers({ record });

  // Numeric ids only. A record resolving to Wealthbox or Redtail already
  // guarantees that; this is the type narrowing, and a guard for the day a
  // record resolves to something else.
  const assignees = useMemo(
    () =>
      (assigneesData?.data ?? []).filter(
        (assignee): assignee is CrmUser & { id: number } =>
          typeof assignee.id === "number",
      ),
    [assigneesData?.data],
  );

  // Same rule as the Slant form: a connected CRM always has at least one user,
  // so an empty list is a fault. Rendering a required Assignee over an empty
  // dropdown would leave the form unsubmittable and only obliquely explained,
  // which is the defect this PR exists to remove (R9).
  const assigneesUnavailable = isErrorAssignees || assignees.length === 0;

  const assigneeOptions = useMemo(
    () =>
      assignees
        .slice()
        .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
        .map((assignee) => (
          <option key={assignee.id} value={assignee.id}>
            {assignee.name}
          </option>
        )),
    [assignees],
  );

  const isPending = isPendingAssignees || isPendingCategories;

  useEffect(() => {
    if (
      isFetchedAssignees &&
      assignees.length > 0 &&
      isNaN(getValues().assignedTo)
    ) {
      setValue("assignedTo", assignees[0].id);
    }
  }, [isFetchedAssignees, assignees, setValue, getValues]);

  if (isPending) {
    return <Loading />;
  }

  if (assigneesUnavailable) {
    return (
      <InlineError>
        Assignees could not be loaded, so a task cannot be created right now.
        Check that the CRM connection is still valid.
      </InlineError>
    );
  }

  return (
    <Form onSubmit={handleSubmit((data) => submit(data))}>
      {errorMessage}
      <Form.Group className="mb-3" controlId="form-name">
        <Form.Label>{getSchemaFieldLabel(schema.fields.name)}</Form.Label>
        <Form.Control type="text" {...register("name")} />
        <FormFieldError field={errors.name} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="form-description">
        <Form.Label>
          {getSchemaFieldLabel(schema.fields.description)}
        </Form.Label>
        <Form.Control type="text" {...register("description")} />
        <FormFieldError field={errors.description} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="form-dueDate">
        <Form.Label>{getSchemaFieldLabel(schema.fields.dueDate)}</Form.Label>
        <Form.Control type="date" {...register("dueDate")} />
        <FormFieldError field={errors.dueDate} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="form-assignee">
        <Form.Label>{getSchemaFieldLabel(schema.fields.assignedTo)}</Form.Label>
        <Form.Select {...register("assignedTo")}>{assigneeOptions}</Form.Select>
        <FormFieldError field={errors.assignedTo} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="form-category">
        <Form.Label>{getSchemaFieldLabel(schema.fields.category)}</Form.Label>
        {isErrorCategories ? (
          <InlineError>There was an error loading categories</InlineError>
        ) : (
          <Form.Select {...register("category")}>{categoryOptions}</Form.Select>
        )}
        <FormFieldError field={errors.category} />
      </Form.Group>
      <Form.Group className="mb-3" controlId="form-priority">
        <Form.Label>{getSchemaFieldLabel(schema.fields.priority)}</Form.Label>
        <Form.Select {...register("priority")}>
          <option value="">Select a priority...</option>
          <option value="Low">Low</option>
          <option value="Medium">Medium</option>
          <option value="High">High</option>
          {crm.slant ? <option value="Urgent">Urgent</option> : null}
        </Form.Select>
        <FormFieldError field={errors.priority} />
      </Form.Group>
      <SubmitButton isSubmitting={isSubmitting} label="Create" />
    </Form>
  );
};

export default CreateTask;
